import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Button, Chip, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useRecordContext } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';
import { IntegrationType } from '../../models';

/**
 * Valida no EVO se o CNPJ do posto está habilitado, na hora em que a integração é escolhida.
 *
 * Sem isso, só se descobre que o EVO não atende aquele CNPJ quando um cliente tenta resgatar e
 * leva um erro — o backend só chama o parceiro no resgate.
 *
 * Cada checagem EMITE uma cortesia de verdade no EVO: é o único jeito de saber, porque o EVO
 * não tem endpoint de consulta e o login valida só as credenciais globais do PontuaX. O ticket
 * é descartado (expira sozinho), mas consome cota do posto — daí a checagem automática rodar
 * UMA vez, e só quando o usuário muda o seletor para EVO.
 *
 * A rota é `POST /stations/integration-check`, e não a do relatório de integrações: quem edita o
 * posto precisa poder testar o que está escolhendo, e o relatório é `isAdmin` puro.
 */

const EVO_CHECK_FIELD = 'evoCheckStatus';

/**
 * Bloqueia o salvar só quando o EVO RECUSOU A EMISSÃO para este CNPJ. Posto antigo já salvo como
 * EVO, que nunca foi testado, continua editável — do contrário qualquer edição dele travaria até
 * alguém clicar no botão, queimando uma cortesia à toa. Falha de login também não trava: é
 * credencial global do ambiente e não diz nada sobre este CNPJ (ver `publishStatus` abaixo).
 *
 * O texto é curto de propósito: ele responde "por que o salvar travou". O motivo da recusa fica
 * na legenda ao lado do chip, que é quem tem espaço para a frase inteira.
 */
export const validateEvoCnpjCheck = (value, allValues) =>
  value === IntegrationType.evo && allValues?.[EVO_CHECK_FIELD] === 'fail'
    ? 'Teste do EVO falhou.'
    : undefined;

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const formatExpiry = (epochSeconds) => {
  if (!epochSeconds) return null;
  return new Date(epochSeconds * 1000).toLocaleDateString('pt-BR');
};

const successLabel = (result) => {
  const expiry = formatExpiry(result.expiresAt);
  return `CNPJ habilitado — cortesia ${result.ticket}${expiry ? ` (expira em ${expiry})` : ''}`;
};

/**
 * O `Grid` do item mora aqui dentro, e não no formulário: o componente fica MONTADO mesmo quando
 * a integração não é EVO — é o que faz "já testei este CNPJ" sobreviver a trocar de integração e
 * voltar, sem emitir outra cortesia — e um Grid vazio na linha empurraria os campos de token do
 * RTI/Trevo para a linha de baixo.
 */
export const EvoCnpjCheck = ({ size = { xs: 12, md: 6 } }) => {
  const record = useRecordContext();
  const { setValue, getValues, trigger } = useFormContext();
  const integration = useWatch({ name: 'integration' });
  const cnpj = useWatch({ name: 'cnpj' });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const digits = onlyDigits(cnpj);
  const isEvo = integration === IntegrationType.evo;
  // Posto que JÁ estava salvo como EVO não dispara nada sozinho: abrir a tela não é escolher a
  // integração. Em posto novo o record não existe, então qualquer escolha de EVO é mudança.
  const wasSavedAsEvo = record?.integration === IntegrationType.evo;

  // `autoRunDone` nunca é resetado: a checagem automática é a PRIMEIRA vez que o seletor vira
  // EVO nesta tela. Sair para RTI e voltar não emite outra cortesia — pra isso existe o botão.
  const autoRunDone = useRef(false);
  const checkedCnpj = useRef(null);
  const publishedStatus = useRef(undefined);

  const publishStatus = useCallback((status) => {
    if (publishedStatus.current === status) return;
    publishedStatus.current = status;
    // shouldDirty: false — a checagem não é edição do posto, não pode sujar o formulário.
    setValue(EVO_CHECK_FIELD, status, { shouldDirty: false });
    trigger('integration');
  }, [setValue, trigger]);

  const runCheck = useCallback(async () => {
    const cnpjToCheck = onlyDigits(getValues('cnpj'));
    if (cnpjToCheck.length !== 14) return;

    setLoading(true);
    setError(null);
    setStale(false);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/stations/integration-check`,
        {
          method: 'POST',
          body: JSON.stringify({
            integration: IntegrationType.evo,
            cnpj: cnpjToCheck,
            placeId: getValues('id') || undefined,
          }),
        }
      );
      const data = json?.data?.[0] ?? json;
      checkedCnpj.current = cnpjToCheck;
      setResult(data);
      // Só a recusa da EMISSÃO fala sobre este CNPJ. Login quebrado é credencial global do
      // ambiente: mesmo raciocínio do catch abaixo, não dá para concluir nada, então não trava.
      if (data?.ok) {
        publishStatus('ok');
      } else {
        publishStatus(data?.step === 'login' ? undefined : 'fail');
      }
    } catch (err) {
      // Erro da nossa API (rede, 429, 500) não é recusa do EVO: não dá para concluir nada sobre
      // o CNPJ, então não bloqueia o salvar.
      checkedCnpj.current = cnpjToCheck;
      setResult(null);
      setError(err?.body?.message || err?.message || 'Não foi possível validar o CNPJ no EVO');
      publishStatus(undefined);
    } finally {
      setLoading(false);
    }
  }, [getValues, publishStatus]);

  // Disparo automático: uma vez, quando o seletor muda para EVO (vindo do vazio ou de outra
  // integração já salva). Se o CNPJ ainda não estiver completo, espera ele ficar.
  useEffect(() => {
    if (!isEvo || wasSavedAsEvo || autoRunDone.current || digits.length !== 14) return;
    autoRunDone.current = true;
    runCheck();
  }, [isEvo, wasSavedAsEvo, digits, runCheck]);

  // CNPJ mudou depois de uma checagem: o resultado é de outro CNPJ. Invalida (o que também solta
  // o bloqueio do salvar, já que a recusa era do CNPJ antigo) e espera o clique no botão.
  useEffect(() => {
    if (!checkedCnpj.current || digits === checkedCnpj.current) return;
    setResult(null);
    setError(null);
    setStale(true);
    publishStatus(undefined);
  }, [digits, publishStatus]);

  if (!isEvo) return null;

  const incomplete = digits.length !== 14;

  return (
    <Grid size={size}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/*
          A faixa do chip + botão imita o campo ao lado (SelectInput do react-admin é filled
          dense: 8px de margem + 48px de altura), para os dois ficarem na mesma linha ótica. O
          alinhamento morava no container de fora, que envolve também a legenda — então o bloco
          inteiro subia e descia conforme o texto crescia.

          O chip nunca encolhe: o rótulo é a informação, cortá-lo com reticências não serve de
          nada. Se algum dia faltar largura de verdade, quem vai para a linha de baixo é o botão.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 48, mt: 1 }}>
          {loading && <Chip size="small" icon={<CircularProgress size={14} />} label="Validando no EVO..." />}

          {!loading && result?.ok && (
            <Tooltip title={successLabel(result)}>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
                label={successLabel(result)}
                sx={{ flexShrink: 0 }}
              />
            </Tooltip>
          )}

          {/*
            Rótulo curto e por PASSO: dizer "EVO recusou" quando a falha foi no login acusa o
            CNPJ do posto por um problema de credencial do ambiente. O erro cru do EVO vai para
            o tooltip, para o chip não crescer e empurrar o botão para a linha de baixo.
          */}
          {!loading && result && !result.ok && (
            <Tooltip title={result.attempt?.errorMessage || ''}>
              <Chip
                size="small"
                color={result.step === 'login' ? 'warning' : 'error'}
                variant="outlined"
                icon={<ErrorOutlineIcon />}
                label={result.step === 'login' ? 'Falha no acesso ao EVO' : 'EVO recusou o CNPJ'}
                sx={{ flexShrink: 0 }}
              />
            </Tooltip>
          )}

          {!loading && !result && error && (
            <Tooltip title={error}>
              <Chip size="small" color="warning" variant="outlined" icon={<ErrorOutlineIcon />} label={error} sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}

          <Tooltip title="Cada teste emite uma cortesia de verdade no EVO (o ticket é descartado e expira sozinho).">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={runCheck}
                disabled={loading || incomplete}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {result || error ? 'Testar de novo' : 'Testar EVO'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {incomplete && (
          <Typography variant="caption" color="text.secondary">
            Informe o CNPJ completo para validar no EVO.
          </Typography>
        )}
        {!incomplete && stale && !loading && (
          <Typography variant="caption" color="text.secondary">
            CNPJ alterado — teste de novo.
          </Typography>
        )}
        {!loading && result && !result.ok && result.diagnosis && (
          <Typography variant="caption" color="text.secondary">{result.diagnosis}</Typography>
        )}
      </Box>
    </Grid>
  );
};

export default EvoCnpjCheck;
